'use client';

import React, { useMemo } from 'react';
import { useGlobalUI } from 'contexts/GlobalUIContext';
import { Container } from '..';
import { CustomImage as Image } from '..';
import { P } from './styleConstants';
import styles from './CnOComparison.module.css';
import { NewCheckmark } from 'assets/icons/new_checkmark';
import { useIsCnOPlus } from 'hooks/pricing/useIsCnOPlus';

interface Feature {
  name: string;
  ultrahuman: boolean | string;
  others: boolean | string;
  pro?: boolean;
}

const competitors = [{ key: 'others' as keyof Feature, name: 'Others' }];

export const CnOComparison = ({ className }: { className?: string }) => {
  const GlobalUI = useGlobalUI();
  const { getValue, isCnOPlus } = useIsCnOPlus();

  const renderValue = (value: boolean | string | undefined, uh?: boolean) => {
    if (typeof value === 'boolean') {
      return value ? (
        // <span className="checkmark">yes</span>
        <div className="img-container">
          <NewCheckmark
            fill={uh ? '#235eff' : 'black'}
            className={styles.checkMark}
          />
        </div>
      ) : (
        // <span className="cross">no</span>
        <div className="img-container">
          <Image
            src="web_v2/ring-buy/comparison/crossmark_new.svg"
            width={20}
            height={20}
            alt="cross-icon"
            className="cross-icon"
          />
        </div>
      );
    }

    if (typeof value === 'string') {
      return <span className={styles.featureValue}>{value}</span>;
    }

    return value;
  };

  const features = useMemo(() => {
    return [
      {
        name: getValue('Period prediction', 'Period prediction'),
        ultrahuman: true,
        others: true,
      },
      {
        name: getValue(
          'Ovulation day prediction',
          'Ovulation phase estimation',
        ),
        ultrahuman: true,
        others: true,
      },
      {
        name: getValue(
          'Ovulation confirmation with over 90% accuracy',
          'Ovulation pattern estimation with 90%+ consistency',
        ),
        ultrahuman: true,
        others: false,
        pro: true,
      },
      {
        name: getValue(
          'Anovulatory cycle detection',
          'Estimates possible non-ovulatory cycles',
        ),
        ultrahuman: true,
        others: false,
        pro: true,
      },
      {
        name: getValue(
          'Support for irregular and diverse cycles',
          'Support for irregular and diverse cycles',
        ),
        ultrahuman: true,
        others: false,
        pro: true,
      },
      {
        name: getValue(
          'Proprietary Cycle Flags™',
          'Cycle Flags, highlighting unique cycle patterns',
        ),
        ultrahuman: true,
        others: false,
        pro: true,
      },
      {
        name: getValue(
          'Predictions for your next 12 cycles',
          'Estimated your next 12 cycles',
        ),
        ultrahuman: true,
        others: false,
        pro: true,
      },
      ...(!isCnOPlus
        ? [
            {
              name: 'Global medical standards',
              ultrahuman: true,
              others: false,
              pro: true,
            },
          ]
        : []),
      {
        name: getValue('HSA/FSA eligibility', 'HSA/FSA eligibility'),
        ultrahuman: true,
        others: false,
        pro: true,
      },
    ];
  }, [getValue, isCnOPlus]);

  const visibleCompetitors = competitors;

  return (
    <section
      className={
        className ? `${styles.cnoComparison} ${className}` : styles.cnoComparison
      }
    >
      <Container>
        <div className={styles.headSection}>
          <h2>How we compare</h2>
          <P>
            {getValue(
              'See how Cycle and Ovulation Pro compare with other traditional trackers in the market.',
              'See how Cycle and Ovulation Plus compare with other traditional trackers in the market.',
            )}
          </P>
        </div>

        <div
          className={styles.stickyTableHeader}
          style={{
            top: `${GlobalUI.headerVisible ? GlobalUI.headerHeight : 0}px`,
          }}
        >
          <div className={`${styles.headerCell} ${styles.featureHeader}`}></div>
          <div className={`${styles.headerCell} ${styles.ultrahumanHeader}`}>
            {getValue('Cycle & Ovulation Pro', 'Cycle & Ovulation Plus')}
          </div>
          {visibleCompetitors.map((competitor) => (
            <div key={competitor.key} className={styles.headerCell}>
              {competitor.name}
            </div>
          ))}
        </div>

        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.featureCol}></th>
                <th className={styles.ultrahumanCol}>
                  {getValue('Cycle & Ovulation Pro', 'Cycle & Ovulation Plus')}
                </th>
                {visibleCompetitors.map((competitor) => (
                  <th key={competitor.key}>{competitor.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {features.map((feature, index) => (
                <tr key={index}>
                  <td className={styles.featureCol}>
                    {feature.pro ? (
                      <>
                        <span className={styles.pro}>
                          {getValue('pro exclusive', 'plus exclusive')}
                        </span>{' '}
                        <br />
                      </>
                    ) : null}
                    {feature.name}
                  </td>
                  <td className={styles.ultrahumanCol}>
                    {renderValue(feature.ultrahuman, true)}
                  </td>
                  {visibleCompetitors.map((competitor) => (
                    <td key={competitor.key}>
                      {renderValue(feature[competitor.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div
          className={styles.mobileStickyHeader}
          style={{
            top: `${GlobalUI.headerVisible ? GlobalUI.headerHeight : 0}px`,
          }}
        >
          <div className={styles.headerCell} style={{ textAlign: 'left' }}></div>
          {/* FIXME: PRO_PLUS */}
          <div className={styles.headerCell}>
            C&O{' '}
            <Image
              src={getValue(
                `web_v2/womens-health/hero/pro.png`,
                `web_v2/womens-health/plus/misc/Plus.png`,
              )}
              alt="pro"
              height={17}
              width={30}
              style={{
                display: 'inline-block',
                verticalAlign: 'middle',
              }}
            />
          </div>
          <div className={styles.headerCell}>Others</div>
        </div>

        <div className={styles.mobileContainer}>
          <table className={styles.mobileTable}>
            <thead>
              <tr>
                <th></th>
                <th>
                  C&O{' '}
                  <Image
                    src={`web_v2/womens-health/hero/pro.png`}
                    alt="pro"
                    height={48}
                    width={84}
                    style={{
                      display: 'inline-block',
                      verticalAlign: 'middle',
                    }}
                  />
                </th>
                <th>Others</th>
              </tr>
            </thead>
            <tbody>
              {features.map((feature, index) => (
                <tr key={index} className={styles.featureRow}>
                  <td>
                    {feature.pro ? (
                      <>
                        <span className={styles.pro}>
                          {getValue('pro exclusive', 'plus exclusive')}
                        </span>{' '}
                        <br />
                      </>
                    ) : null}
                    {feature.name}
                  </td>
                  <td>{renderValue(feature.ultrahuman, true)}</td>
                  <td>{renderValue(feature.others)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Container>
    </section>
  );
};
